import { useState, useEffect } from "react";
import apiClient from "../../api-services/ApiClient";
import AdminLayout from "../../app-components/AdminLayout";
import {
  Icon_Active_Users,
  Icon_Removed_Users,
  Icon_TotalUsers,
  Icon_Total_Revenue,
} from "../../assets/Icons";
import CurrencyToggle from "../../app-components/CurrencyToggle";
import useCurrency from "../../hooks/useCurrency";
import { formatUsd, twoDecimalPlace } from "../../utils";
import LineChartComponent from "../../app-components/recharts-components/LineChart";

interface dashboardInterface {
  total_revenue: number;
  total_revenue_usd?: number | null; // from backend
  total_users: number;
  active_users: number;
  removed_users: number;
  revenue_overview: any; //yet to be populated
  device_traffic: any; //yet to be populated
  connection_requests_by_gender: any;
}

interface FormStat {
  year: string;
  monthName: string;
  monthNumber: number;
  signupFemales: number;
  signupMales: number;
  signupOther: number;
}
type FormStatsType = FormStat[];

const Dashboard = () => {
  const [currency, setCurrency] = useCurrency();
  const [formStats, setFormStats] = useState<FormStatsType>([]);
  const [dashboardData, setDashboardData] = useState<dashboardInterface>();

  useEffect(() => {
    getDashboard();
  }, []);

  const getDashboard = async () => {
    const res: any = await apiClient.get("dashboard");
    setDashboardData(res.data);
    setFormStats(res.data.form_stats);
  };

  return (
    <AdminLayout title="Admin | Dashboard">
      <div className="flex justify-between items-center">
        <p className="font-semibold text-[25px]">Dashboard</p>
        <CurrencyToggle currency={currency} onChange={setCurrency} />
      </div>

      {/* <!-- count stat --> */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mt-3">
        <div className="flex flex-col gap-3 bg-white rounded-10px p-6">
          <img
            src={Icon_Total_Revenue}
            alt=""
            className="w-12 bg-[#D9D9D9] p-3 rounded-10px"
          />
          <p className="font-medium text-base text-honeConnectGray">
            Total Revenue
          </p>
          <p className="font-semibold text-3xl text-honeConnectBlack">
            {currency === "USD"
              ? formatUsd(dashboardData?.total_revenue_usd)
              : `₦${twoDecimalPlace(dashboardData?.total_revenue ?? 0)}`}
          </p>
        </div>
        <div className="flex flex-col gap-3 bg-white rounded-10px p-6">
          <img
            src={Icon_TotalUsers}
            alt=""
            className="w-12 bg-[#D9D9D9] p-3 rounded-10px"
          />
          <p className="font-medium text-base text-honeConnectGray">
            Total Users
          </p>
          <p className="font-semibold text-3xl text-honeConnectBlack">
            {dashboardData?.total_users.toLocaleString() ?? 0}
          </p>
        </div>
        <div className="flex flex-col gap-3 bg-white rounded-10px p-6">
          <img
            src={Icon_Active_Users}
            alt=""
            className="w-12 bg-[#D9D9D9] p-3 rounded-10px"
          />
          <p className="font-medium text-base text-honeConnectGray">
            Active Users
          </p>
          <p className="font-semibold text-3xl text-honeConnectBlack">
            {dashboardData?.active_users.toLocaleString() ?? 0}
          </p>
        </div>
        <div className="flex flex-col gap-3 bg-white rounded-10px p-6">
          <img
            src={Icon_Removed_Users}
            alt=""
            className="w-12 bg-[#D9D9D9] p-3 rounded-10px"
          />
          <p className="font-medium text-base text-honeConnectGray">
            Removed Users
          </p>
          <p className="font-semibold text-3xl text-honeConnectBlack">
            {dashboardData?.removed_users.toLocaleString() ?? 0}
          </p>
        </div>
      </div>

      {/* <!-- line graph --> */}
      <div className="bg-white rounded-10px p-6 pb-16 mt-5">
        <p className="font-medium text-[20px] mb-10">
          Users Gender Sign Ups By Month{" "}
        </p>
        <div className="h-60">
          <div className="h-full flex items-center justify-center mt-14 relative left-[-20px]">
            <LineChartComponent data={formStats} />
          </div>
        </div>
      </div>

      {/* <!-- progress bar --> */}
      <div className="bg-white rounded-10px p-6 mt-5">
        <p className="font-semibold text-[20px] mb-5">Request & Posts</p>

        <div className="flex gap-5">
          <div className="w-[100px]">
            <p className="font-semibold text-[16px] mb-2">Gender</p>
            <p className="font-medium text-[14px] mb-4">
              Male ({dashboardData?.connection_requests_by_gender?.MALE})
            </p>
            <p className="font-medium text-[14px] mb-4">
              Female ({dashboardData?.connection_requests_by_gender?.FEMALE})
            </p>
          </div>

          <div className="w-full">
            <p className="font-semibold text-[16px] mb-3">&nbsp;</p>
            <div className="font-medium text-[14px] mb-8 w-full h-1 bg-gray-300">
              <p
                className="font-medium text-[14px] mb-6 h-1 bg-honeConnectPurple rounded-lg"
                style={{
                  width: `${
                    dashboardData?.connection_requests_by_gender?.MALE &&
                    dashboardData?.connection_requests_by_gender?.FEMALE
                      ? (dashboardData.connection_requests_by_gender.MALE /
                          (dashboardData.connection_requests_by_gender.MALE +
                            dashboardData.connection_requests_by_gender
                              .FEMALE)) *
                        100
                      : 0
                  }%`,
                }}
              >
                &nbsp;
              </p>
            </div>

            <div className="font-medium text-[14px] mb-8 w-full h-1 bg-gray-300">
              <p
                className="font-medium text-[14px] mb-6 h-1 bg-honeConnectOrange rounded-lg"
                style={{
                  width: `${
                    dashboardData?.connection_requests_by_gender?.FEMALE &&
                    dashboardData?.connection_requests_by_gender?.MALE
                      ? (dashboardData.connection_requests_by_gender.FEMALE /
                          (dashboardData.connection_requests_by_gender.MALE +
                            dashboardData.connection_requests_by_gender
                              .FEMALE)) *
                        100
                      : 0
                  }%`,
                }}
              >
                &nbsp;
              </p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
